import { Outlet, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// NOTE: QueryClient is created per-render inside Root (lazy useState) rather
// than as a module singleton. The SSR Node service is long-lived, so a shared
// client would leak one request's cache into the next (same query key →
// stale cross-request data). Per-render gives a fresh cache per SSR request
// and a stable cache per client mount.
import { BrandingProvider } from "@/providers/BrandingProvider";
import { normalizeSessionUser, useSession } from "@/store/useSession";
import { client } from "@/api/api";
import { isPublicAppRoute, isWelcomeRoute } from "@/lib/routes";
import { captureUTMCookie } from "@/lib/utm-cookie";
import { shouldRedirectToUpgrade } from "@/lib/upgradeStatus";
import ErrorPage from "@/pages/ErrorPage";
import WorkspaceStatus from "@/components/WorkspaceStatus";
import FrontendSessionBar from "@/components/FrontendSessionBar";
import {
  completeSilentAuth,
  isLikelyBotUserAgent,
  recoverUnauthorizedSession,
  resetPassiveAuthAttempt,
} from "@/lib/silent-auth";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function getCookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;
  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return cookie ? cookie.split("=")[1] : null;
}

function clearCookie(name: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Max-Age=0; path=/`;
}

function isTemplateSetupSession(
  user: { id?: string; email?: string } | null | undefined,
): boolean {
  if (!user) return false;
  return (
    Boolean(user.id?.startsWith("template-admin-")) ||
    user.email === "__superuser__"
  );
}

export function SessionGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { isLoading, hasResolvedSession, restoreCachedUser, setUser, setLoading } =
    useSession();

  const [serverError, setServerError] = useState<{
    code: number;
    pathname: string;
  } | null>(() => {
    const errorCookie = getCookieValue("foxia_error_code");
    if (errorCookie) {
      const code = parseInt(errorCookie);
      if (!isNaN(code)) return { code, pathname: location.pathname };
    }
    return null;
  });

  useEffect(() => {
    captureUTMCookie();
    restoreCachedUser();
  }, [restoreCachedUser]);

  const { pathname } = location;
  const publicRoute = isPublicAppRoute(pathname);
  const passiveLandingRoute =
    pathname === "/welcome" ||
    (pathname === "/" && isWelcomeRoute(pathname));

  useEffect(() => {
    if (publicRoute) {
      if (isWelcomeRoute(pathname) || pathname.startsWith("/auth/")) {
        clearCookie("foxia_error_code");
      }
      if (!passiveLandingRoute || hasResolvedSession) return;
      // A clean /welcome visit clears only the per-tab SDK guard. The passive
      // cooldown cookie remains, preventing a login_required callback loop.
      if (!location.search && !location.hash) resetPassiveAuthAttempt();
      // Prerendered landing HTML is already complete for crawlers. Do not
      // make bots call the session endpoint or enter any auth flow after
      // hydration; only visible, top-level human browsers may do that.
      if (isLikelyBotUserAgent()) return;

      let cancelled = false;
      const checkLandingSession = async () => {
        try {
          const res = await client.get("/gateway/me");
          const sessionUser = normalizeSessionUser(res.data);
          if (!sessionUser) {
            const passiveState = await recoverUnauthorizedSession({
              mode: "passive",
              shouldCancel: () => cancelled,
            });
            if (passiveState === "redirecting" || passiveState === "cancelled") return;
            if (!cancelled) setUser(null);
            return;
          }
          if (!cancelled) {
            completeSilentAuth();
            setUser(sessionUser);
          }
        } catch (err) {
          const status = (err as { response?: { status?: number } }).response?.status;
          if (status === 401 && !cancelled) {
            const passiveState = await recoverUnauthorizedSession({
              mode: "passive",
              shouldCancel: () => cancelled,
            });
            if (passiveState === "redirecting" || passiveState === "cancelled") return;
          }
          if (!cancelled) setUser(null);
        }
      };

      checkLandingSession();
      return () => {
        cancelled = true;
      };
    }

    if (hasResolvedSession) return;

    let cancelled = false;

    const checkStatus = async () => {
      const errorCookie = getCookieValue("foxia_error_code");
      if (!errorCookie || cancelled) return;
      const code = parseInt(errorCookie);
      if (isNaN(code)) return;
      clearCookie("foxia_error_code");

      try {
        const upgrade = await client.get("/gateway/me/upgrade-status");
        if (!cancelled && shouldRedirectToUpgrade(upgrade.data)) {
          window.location.href = "/upgrade";
          return;
        }
      } catch (err) {
        console.error("Failed to check upgrade status after server error", err);
      }

      if (code === 502 || code === 503) {
        try {
          const res = await client.get("/gateway/me/provisioning-status");
          const status = res.data?.status;
          if (
            !cancelled &&
            (status === "starting" ||
              status === "provisioning" ||
              status === "pending")
          ) {
            window.location.href = "/setup";
            return;
          }
        } catch (err) {
          console.error(
            "Failed to check provisioning status after upstream error",
            err,
          );
        }
      }

      if (!cancelled) setServerError({ code, pathname });
    };

    const checkSession = async () => {
      setLoading(true);
      try {
        const res = await client.get("/gateway/me");
        const sessionUser = normalizeSessionUser(res.data);
        if (!sessionUser) {
          const authState = await recoverUnauthorizedSession({ mode: "interactive" });
          if (authState === "redirecting") return;
          if (!cancelled) setUser(null);
          return;
        }
        const hasNeedsOnboardingCookie =
          getCookieValue("foxia_needs_onboarding") === "true";
        if (hasNeedsOnboardingCookie) clearCookie("foxia_needs_onboarding");
        if (hasNeedsOnboardingCookie && !isTemplateSetupSession(sessionUser)) {
          window.location.href = "/onboarding";
          return;
        }
        if (!cancelled) {
          completeSilentAuth();
          setUser(sessionUser);
        }
      } catch (err) {
        console.error("Session check failed", err);
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 401 && !cancelled) {
          const authState = await recoverUnauthorizedSession({ mode: "interactive" });
          if (authState === "redirecting") return;
        }
        if (!cancelled) setUser(null);
      }
      await checkStatus();
    };

    checkSession();
    return () => {
      cancelled = true;
    };
  }, [
    hasResolvedSession,
    location.hash,
    location.search,
    passiveLandingRoute,
    pathname,
    publicRoute,
    setLoading,
    setUser,
  ]);

  if (serverError?.pathname === pathname && !publicRoute) {
    if (serverError.code === 502) return <WorkspaceStatus />;
    return <ErrorPage code={serverError.code} />;
  }

  if (
    !publicRoute &&
    getCookieValue("foxia_license_expired") === "true"
  ) {
    return <WorkspaceStatus />;
  }

  if (!publicRoute && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center font-medium text-zinc-500">
        Đang tải phiên làm việc...
      </div>
    );
  }

  return <>{children}</>;
}

// Home redirect: inside ProtectedRoute, determines onboarding vs setup
export function HomeRedirect() {
  const { user } = useSession();
  if (user?.needs_onboarding && !isTemplateSetupSession(user)) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Navigate to="/setup" replace />;
}

export function Root() {
  // Per-render QueryClient: on the long-lived SSR Node server a module
  // singleton would leak one request's cache into the next (same query keys).
  // useState initializer runs once per mount on the client, once per render
  // on the server — exactly the lifecycle we want.
  const [queryClient] = useState(() => new QueryClient());
  const location = useLocation();
  const publicRoute = isPublicAppRoute(location.pathname);
  const showSessionBar = useSession((state) => Boolean(state.user));

  return (
    <QueryClientProvider client={queryClient}>
      <BrandingProvider>
        <SessionGate>
          {showSessionBar && !publicRoute && <FrontendSessionBar />}
          <ScrollToTop />
          <div className={showSessionBar && !publicRoute ? "pt-14" : undefined}>
            <Outlet />
          </div>
        </SessionGate>
      </BrandingProvider>
    </QueryClientProvider>
  );
}
